'use client'

import { useState } from 'react'

import { useToolUsage } from '@/lib/use-tool-usage'
import { useToolAccessGate } from '@/components/tools/ToolAccessGate'
import { UsageLimitGate } from '@/components/tools/UsageLimitGate'

import ToolSiteFreshnessAuditorRef from '@/components/reference/ToolSiteFreshnessAuditorRef'
import { useUrlRun } from '@/components/reference/tool-run'
import '@/components/reference/tool-page.css'
import { ToolLiveStatus } from '@/components/reference/tool-live'
import '@/components/reference/tool-live.css'

const SCORE_CATEGORIES = [
  {
    key: 'sitemapFreshness',
    label: 'Sitemap Freshness',
    color: '#008dcc',
    pts: '25 pts',
    desc: 'Does /sitemap.xml exist? What percentage of URLs have <lastmod> tags? How recent are those dates? Agents use the sitemap as their primary change-detection manifest.',
  },
  {
    key: 'feedAvailability',
    label: 'Feed Availability',
    color: '#FDC304',
    pts: '15 pts',
    desc: 'Is there a reachable RSS or Atom feed? Is it linked from the homepage via <link rel="alternate">? Subscription-based agents prefer feeds over repeated sitemap diffs.',
  },
  {
    key: 'blogCadence',
    label: 'Blog Cadence',
    color: '#F90B8A',
    pts: '25 pts',
    desc: 'How many posts in the last 30, 60, 90 days? 3-5 fresh posts per month is the minimum for category visibility. Cadence beats volume because each post is a verifiable change event.',
  },
  {
    key: 'articleSchemaCoverage',
    label: 'Article Schema Coverage',
    color: '#8b5cf6',
    pts: '20 pts',
    desc: 'Across sampled URLs: do they have Article or BlogPosting JSON-LD with dateModified? Is dateModified recent? This is the per-page freshness anchor agents extract.',
  },
  {
    key: 'visibleDateStamps',
    label: 'Visible Date Stamps',
    color: '#22c55e',
    pts: '15 pts',
    desc: 'Does the rendered HTML have visible <time> elements or "Last updated" copy? AI Overview citations pull "as of [date]" framing from visible page text, not from JSON-LD.',
  },
] as const

interface Finding {
  text: string
  status: 'good' | 'warning' | 'bad'
}

interface CategoryResult {
  score: number
  maxScore: number
  color: string
  findings: Finding[]
}

interface FreshnessAuditResult {
  url: string
  domain: string
  checkedAt: string
  overallScore: number
  grade: 'A' | 'B' | 'C' | 'D' | 'F'
  categories: Record<string, CategoryResult>
  detected: Record<string, unknown>
  recommendations: {
    priority: 'high' | 'medium' | 'low'
    category: string
    message: string
  }[]
}

const STATUS_STYLES: Record<Finding['status'], string> = {
  good: 'text-foreground',
  warning: 'text-foreground/80',
  bad: 'text-foreground',
}

const STATUS_DOT: Record<Finding['status'], string> = {
  good: 'bg-[#22c55e]',
  warning: 'bg-[#FDC304]',
  bad: 'bg-[#F90B8A]',
}

const GRADE_COLOR: Record<FreshnessAuditResult['grade'], string> = {
  A: '#22c55e',
  B: '#008dcc',
  C: '#FDC304',
  D: '#f97316',
  F: '#F90B8A',
}

export default function SiteFreshnessAuditorPage() {
  const [result, setResult] = useState<FreshnessAuditResult | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const toolUsage = useToolUsage('site-freshness-auditor')
  const gate = useToolAccessGate(
    'site-freshness-auditor',
    'Site Freshness Auditor',
    {
      variant: 'reference',
    }
  )

  // Input validation lives in ToolEntryHero; this receives the trimmed URL.
  async function handleSubmit(trimmed: string) {
    if (!gate.ready) {
      // Resumes after the code verifies, so the typed URL runs (PIX-47).
      gate.openGate(trimmed, () => void runCheck(trimmed))
      return
    }

    await runCheck(trimmed)
  }

  async function runCheck(trimmed: string) {
    setIsLoading(true)
    setError(null)
    setResult(null)

    try {
      const response = await toolUsage.fetchWithUsage(
        '/api/tools/site-freshness-auditor',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ url: trimmed }),
        }
      )

      const data = await response.json()

      if (
        response.status === 403 &&
        (data.error === 'usage_limit' || data.error === 'gate_required')
      ) {
        gate.openGate(trimmed, () => void runCheck(trimmed))
        return
      }

      if (response.status === 429 && data.error === 'domain_cap') {
        gate.showDomainCap(data)
        return
      }

      if (!response.ok) {
        throw new Error(data.message || 'Audit failed. Please try again.')
      }

      setResult(data as FreshnessAuditResult)
      gate.markConsumed()

      setTimeout(() => {
        document
          .getElementById('freshness-results')
          ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }, 100)
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : 'Something went wrong. Please try again.'
      setError(message)
    } finally {
      setIsLoading(false)
    }
  }

  const run = useUrlRun({
    example: 'pixelmojo.io',
    isLoading: isLoading,
    error: error,
    onSubmit: handleSubmit,
  })

  return (
    <div className='shell pg-tool pg-tool-site-freshness-auditor'>
      {gate.modal}
      <ToolSiteFreshnessAuditorRef
        run={run}
        output={
          <>
            <div id='freshness-results' className='max-w-4xl mx-auto'>
              {isLoading && (
                <ToolLiveStatus tool='Site Freshness Auditor'>
                  Fetching sitemap, parsing lastmod tags, detecting feed,
                  sampling recent URLs, checking Article schema and visible
                  dates...
                </ToolLiveStatus>
              )}
              {toolUsage.isGated && !isLoading && (
                <UsageLimitGate
                  variant='reference'
                  toolName='Site Freshness Auditor'
                  used={toolUsage.used}
                  limit={toolUsage.limit}
                />
              )}
              {result && !isLoading && !toolUsage.isGated && (
                <>
                  <div className='bg-muted/50 rounded-2xl border border-line-strong p-8 mb-6 flex flex-col sm:flex-row items-center justify-between gap-6'>
                    <div>
                      <p className='text-sm text-muted-foreground leading-none mb-2'>
                        Audited
                      </p>
                      <p className='text-foreground font-mono text-sm break-all'>
                        {result.url}
                      </p>
                    </div>
                    <div className='flex items-center gap-6'>
                      <div className='text-center'>
                        <p className='text-4xl font-bold text-foreground leading-none'>
                          {result.overallScore}
                          <span className='text-base text-muted-foreground'>
                            /100
                          </span>
                        </p>
                        <p className='text-xs text-muted-foreground mt-1'>
                          Overall
                        </p>
                      </div>
                      <div
                        className='h-16 w-16 rounded-2xl flex items-center justify-center text-3xl font-bold text-background'
                        style={{ backgroundColor: GRADE_COLOR[result.grade] }}
                      >
                        {result.grade}
                      </div>
                    </div>
                  </div>

                  <div className='grid grid-cols-1 md:grid-cols-2 gap-4 mb-6'>
                    {SCORE_CATEGORIES.map(cat => {
                      const cr = result.categories[cat.key]
                      if (!cr) return null
                      const pct = Math.round((cr.score / cr.maxScore) * 100)
                      return (
                        <div
                          key={cat.key}
                          className='bg-background border border-line-strong rounded-2xl p-5'
                        >
                          <div className='flex items-center justify-between mb-3'>
                            <div className='flex items-center gap-2'>
                              <span
                                className='h-2 w-2 rounded-full'
                                style={{ backgroundColor: cat.color }}
                              />
                              <span className='text-sm font-semibold text-foreground leading-none'>
                                {cat.label}
                              </span>
                            </div>
                            <span className='text-sm font-mono text-foreground/80'>
                              {cr.score}/{cr.maxScore}
                            </span>
                          </div>
                          <div className='h-2 w-full rounded-full bg-muted mb-3 overflow-hidden'>
                            <div
                              className='h-full rounded-full'
                              style={{
                                width: `${pct}%`,
                                backgroundColor: cat.color,
                              }}
                            />
                          </div>
                          <ul className='space-y-1.5'>
                            {cr.findings.map((f, i) => (
                              <li
                                key={i}
                                className={`flex items-start gap-2 text-xs leading-relaxed ${STATUS_STYLES[f.status]}`}
                              >
                                <span
                                  className={`flex-shrink-0 w-1.5 h-1.5 rounded-full mt-1.5 ${STATUS_DOT[f.status]}`}
                                />
                                <span>{f.text}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )
                    })}
                  </div>

                  {result.recommendations.length > 0 && (
                    <div className='bg-background border border-line-strong rounded-2xl p-6 mb-6'>
                      <h2 className='text-lg font-semibold text-foreground mb-4'>
                        Recommended fixes
                      </h2>
                      <ol className='space-y-3'>
                        {result.recommendations.map((rec, i) => (
                          <li
                            key={i}
                            className='flex items-start gap-3 text-sm text-foreground leading-relaxed'
                          >
                            <span
                              className='flex-shrink-0 mt-0.5 text-[11px] font-bold rounded-full px-2 py-0.5'
                              style={{
                                backgroundColor:
                                  rec.priority === 'high'
                                    ? '#F90B8A15'
                                    : rec.priority === 'medium'
                                      ? '#FDC30415'
                                      : '#008dcc15',
                                color:
                                  rec.priority === 'high'
                                    ? '#F90B8A'
                                    : rec.priority === 'medium'
                                      ? '#FDC304'
                                      : '#008dcc',
                              }}
                            >
                              {rec.priority.toUpperCase()}
                            </span>
                            <span>
                              <span className='font-semibold'>
                                {rec.category}:
                              </span>{' '}
                              {rec.message}
                            </span>
                          </li>
                        ))}
                      </ol>
                    </div>
                  )}
                </>
              )}
            </div>
          </>
        }
      />
    </div>
  )
}
